/**
 * Enhanced Teacher Dashboard
 * 
 * Displays comprehensive insights for teacher's assigned classes including:
 * - Overall class insights
 * - Subject-wise breakdown
 * - Ability to share class data with other teachers from the same school
 * - View data from both managed and shared classes
 */

import { useState, useEffect, useMemo, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useClassSession } from '../../contexts/ClassSessionContext';
import { RecentSessions } from '../../Components/analytics/RecentSessions';
import { ClassPerformanceWorkspace } from '../../Components/analytics/ClassPerformanceWorkspace';
import { getPassThreshold, getRecentTeacherSessions, type SessionSummaryRow } from '../../services/performanceService';
import { DEFAULT_PASS_THRESHOLD, normaliseScore, type RawScore } from '../../lib/analytics/metrics';
import { DEFAULT_PLAYER } from '../../lib/classroom/resolvePlayerRoute';
import { openLaunchedLessonAsHost } from '../../lib/classroom/openLaunchedLessonAsHost';
import { collection, query, where, orderBy, onSnapshot, doc, updateDoc, getDocs, getDoc, type Unsubscribe } from 'firebase/firestore';
import { shareClass } from '../../services/schoolRosterService';
import { pendingForTeacher } from '../../lib/classroom/students';
import { db } from '../../config/firebase';
import { removeStudentFromSession, approveJoinRequest } from '../../services/classSessionService';
import { VR360_TOUR_CHAPTER_ID, topicIdForVr360TourId, VR360_TOURS } from '../../config/vr360Tours';
import { getApiBaseUrl } from '../../utils/apiConfig';
import { resolveStudentDisplayName } from '../../utils/displayName';
import { StudentScreen360Preview } from '../../Components/StudentScreen360Preview';
import type { Class, StudentScore, LessonLaunch } from '../../types/lms';
import { Link, useNavigate } from 'react-router-dom';
import { learnXRFontStyle, TrademarkSymbol } from '../../Components/LearnXRTypography';
import { SchoolCodeBlock } from '../../Components/SchoolCodeBlock';
import { Card, CardContent } from '../../Components/ui/card';
import { Button } from '../../Components/ui/button';
import { Badge } from '../../Components/ui/badge';
import { Progress } from '../../Components/ui/progress';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../Components/ui/select';
import {
  FaChalkboardTeacher,
  FaUsers,
  FaChartLine,
  FaBook,
  FaUserCheck,
  FaArrowRight,
  FaBell,
  FaShareAlt,
  FaLock,
  FaUnlock,
  FaFilter,
  FaFlask,
  FaMagic,
  FaVideo,
  FaCopy,
  FaStopCircle,
  FaUserMinus,
  FaClipboardList,
  FaFileAlt,
} from 'react-icons/fa';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../Components/ui/dialog';
import { toast } from 'react-toastify';

interface ClassInsight {
  classId: string;
  className: string;
  curriculum: string;
  subject?: string;
  studentCount: number;
  averageScore: number;
  totalQuizzes: number;
  completedLessons: number;
  totalLessons: number;
  completionRate: number;
  isShared: boolean; // Whether this class is shared with the teacher
  isOwner: boolean; // Whether this teacher owns/manages the class
}

const TeacherDashboard = () => {
  const navigate = useNavigate();
  const { user, profile } = useAuth();
  const {
    activeSessionId,
    activeSession,
    progressList,
    startSession,
    launchLesson: launchLessonToClass,
    endSession,
    leaveSessionAsTeacher,
    sessionLoading: sessionContextLoading,
    sessionError: sessionContextError,
    clearSessionError,
  } = useClassSession();
  const [managedClasses, setManagedClasses] = useState<Class[]>([]);
  const [sharedClasses, setSharedClasses] = useState<Class[]>([]);
  const [allTeachers, setAllTeachers] = useState<any[]>([]);
  const [students, setStudents] = useState<any[]>([]);
  const [pendingStudents, setPendingStudents] = useState<any[]>([]);
  const [scores, setScores] = useState<StudentScore[]>([]);
  const [launches, setLaunches] = useState<LessonLaunch[]>([]);
  const [loading, setLoading] = useState(true);
  /** Why a read failed, so an empty page can say so rather than imply no data. */
  const [dataError, setDataError] = useState<string | null>(null);
  /*
    True once the first snapshot has landed.

    The page used to render a full-screen spinner whenever `loading` was true,
    and every re-subscribe set it true again — so a write to the user document
    replaced a correct, populated dashboard with a spinner. After the first load
    there is always something worth showing, and live updates land in place.
  */
  const [hasLoaded, setHasLoaded] = useState(false);

  // Latches on the first completed load and never unlatches, so a later refresh
  // can never put the page back behind a spinner.
  useEffect(() => {
    if (!loading) setHasLoaded(true);
  }, [loading]);


  /*
    The profile fields this screen actually depends on.

    Effects used to depend on the whole `profile` object, which Firestore rebuilt
    on every snapshot of the user document — so anything touching that document,
    including fields nothing here displays, tore down and re-subscribed every
    query on the page.
  */
  const profileRole = profile?.role;
  const profileSchoolId = profile?.school_id;
  const profileManagedSchoolId = profile?.managed_school_id;
  /**
   * A school id discovered from the teacher's classes, to be written once.
   *
   * Kept as state so the write happens in its own effect rather than inside the
   * class subscription: that handler writes to users/{uid}, which is the
   * document the profile subscription watches, so doing it there re-ran the
   * effect that performed it.
   */
  const [schoolIdToBackfill, setSchoolIdToBackfill] = useState<string | null>(null);

  useEffect(() => {
    if (!schoolIdToBackfill || !user?.uid || profileSchoolId) return;
    let cancelled = false;
    void (async () => {
      try {
        await updateDoc(doc(db, 'users', user.uid), {
          school_id: schoolIdToBackfill,
          updatedAt: new Date().toISOString(),
        });
      } catch (error) {
        console.error('TeacherDashboard: Error auto-assigning school_id', error);
      } finally {
        // Once only: the profile snapshot will bring the new value back, and
        // re-running from that would be the loop this moved out of.
        if (!cancelled) setSchoolIdToBackfill(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [schoolIdToBackfill, user?.uid, profileSchoolId]);
  const [sharingClassId, setSharingClassId] = useState<string | null>(null);
  const [selectedSubjectFilter, setSelectedSubjectFilter] = useState<string>('all');
  const [schoolCode, setSchoolCode] = useState<string | null>(null);
  const [studentDisplayNames, setStudentDisplayNames] = useState<Record<string, string>>({});
  const [sessionClassId, setSessionClassId] = useState<string>('');
  const [launchLessonModalOpen, setLaunchLessonModalOpen] = useState(false);
  const [chaptersForLaunch, setChaptersForLaunch] = useState<any[]>([]);
  const [selectedLaunchChapterId, setSelectedLaunchChapterId] = useState<string>('');
  const [selectedLaunchTopicId, setSelectedLaunchTopicId] = useState<string>('');
  /** Which player the whole class opens the lesson in. Rides on launched_lesson. */
  const [launchLessonModalLoading, setLaunchLessonModalLoading] = useState(false);
  const [removingStudentUid, setRemovingStudentUid] = useState<string | null>(null);
  const [studentScreensSkyboxUrl, setStudentScreensSkyboxUrl] = useState<string | null>(null);
  const [studentScreensSkyboxLoading, setStudentScreensSkyboxLoading] = useState(false);
  const [selectedStudentForScreen, setSelectedStudentForScreen] = useState<string | null>(null);

  // Get all classes (managed + shared)
  const allClasses = useMemo(() => {
    return [...managedClasses, ...sharedClasses];
  }, [managedClasses, sharedClasses]);

  // Only requests for classes this teacher teaches are theirs to decide — the
  // same filter as the Approvals tab in My Classes, and what the server enforces.
  const myPendingStudents = useMemo(
    () =>
      pendingForTeacher(
        pendingStudents.map((p) => ({
          id: p.uid,
          approvalStatus: p.approvalStatus ?? 'pending',
          classIds: Array.isArray(p.class_ids) ? p.class_ids : [],
          requestedClassId: p.requested_class_id ?? null,
        })),
        managedClasses.map((c) => c.id)
      ),
    [pendingStudents, managedClasses]
  );

  // Fetch all teachers in the same school
  useEffect(() => {
    if (!user?.uid || !profile || profile.role !== 'teacher' || !profile.school_id) return;

    const teachersQuery = query(
      collection(db, 'users'),
      where('role', '==', 'teacher'),
      where('school_id', '==', profile.school_id)
    );

    const unsubscribeTeachers = onSnapshot(teachersQuery, (snapshot) => {
      const teachersData = snapshot.docs.map(doc => ({
        uid: doc.id,
        ...doc.data(),
      }));
      setAllTeachers(teachersData.filter(t => t.uid !== user.uid)); // Exclude self
    }, (error) => {
      console.error('TeacherDashboard: Error fetching teachers', error);
    });

    return () => unsubscribeTeachers();
  }, [user?.uid, profileRole, profileSchoolId, profileManagedSchoolId]);

  // Fetch school code for display
  useEffect(() => {
    const schoolId = profile?.school_id || profile?.managed_school_id;
    if (!schoolId) return;
    getDoc(doc(db, 'schools', schoolId))
      .then((snap) => {
        if (snap.exists()) setSchoolCode(snap.data()?.schoolCode || null);
      })
      .catch(() => {});
  }, [profile?.school_id, profile?.managed_school_id]);

  // Fetch pending students
  useEffect(() => {
    if (!user?.uid || !profile || profile.role !== 'teacher' || !profile.school_id) return;

    const pendingQuery = query(
      collection(db, 'users'),
      where('role', '==', 'student'),
      where('school_id', '==', profile.school_id),
      where('approvalStatus', '==', 'pending')
    );

    const unsubscribePending = onSnapshot(pendingQuery, (snapshot) => {
      const pendingData = snapshot.docs.map(doc => ({
        uid: doc.id,
        ...doc.data(),
      }));
      setPendingStudents(pendingData);
    }, (error) => {
      console.error('TeacherDashboard: Error fetching pending students', error);
    });

    return () => unsubscribePending();
  }, [user?.uid, profileRole, profileSchoolId, profileManagedSchoolId]);

  // Fetch managed classes (where teacher is in teacher_ids)
  useEffect(() => {
    if (!user?.uid || !profile || profile.role !== 'teacher') {
      setLoading(false);
      return;
    }

    setLoading(true);

    let classesQuery;
    if (profile.school_id) {
      classesQuery = query(
        collection(db, 'classes'),
        where('school_id', '==', profile.school_id),
        where('teacher_ids', 'array-contains', user.uid)
      );
    } else {
      classesQuery = query(
        collection(db, 'classes'),
        where('teacher_ids', 'array-contains', user.uid)
      );
    }

    const unsubscribeClasses = onSnapshot(classesQuery, async (snapshot) => {
      const classesData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
      })) as Class[];

      setManagedClasses(classesData);

      // The school_id back-fill is deliberately NOT done here. Writing to
      // users/{uid} from inside a snapshot handler feeds the very subscription
      // the profile comes from, which re-ran this effect and re-mounted the
      // page. It runs once, outside the subscription, in the effect below.
      if (classesData.length > 0 && !profileSchoolId) {
        setSchoolIdToBackfill(classesData[0]?.school_id ?? null);
      }
    }, (error) => {
      console.error('Error fetching managed classes:', error);
      setLoading(false);
    });

    return () => unsubscribeClasses();
  }, [user?.uid, profileRole, profileSchoolId, profileManagedSchoolId]);

  // Fetch shared classes (where teacher is in shared_with_teachers)
  useEffect(() => {
    if (!user?.uid || !profile || profile.role !== 'teacher' || !profile.school_id) return;

    const sharedClassesQuery = query(
      collection(db, 'classes'),
      where('school_id', '==', profile.school_id),
      where('shared_with_teachers', 'array-contains', user.uid)
    );

    const unsubscribeShared = onSnapshot(sharedClassesQuery, (snapshot) => {
      const sharedData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
      })) as Class[];
      setSharedClasses(sharedData);
    }, (error) => {
      console.error('Error fetching shared classes:', error);
    });

    return () => unsubscribeShared();
  }, [user?.uid, profileRole, profileSchoolId, profileManagedSchoolId]);

  // Fetch students, scores, and launches for all classes
  useEffect(() => {
    if (!user?.uid || !profile || profile.role !== 'teacher' || allClasses.length === 0) {
      if (allClasses.length === 0) setLoading(false);
      return;
    }

    const classIds = allClasses.map(c => c.id);
    const schoolIdForQuery = profile.school_id || allClasses[0]?.school_id;

    /*
      Firestore caps `in` and `array-contains-any` at 30 values.

      A teacher with more classes than that got a rejected query — and since one
      rejection empties `scores`, every section on this page went blank at once,
      which looks exactly like a school where nobody has taken a quiz. Chunked
      into batches of 30 and merged, so the number of classes a teacher has stops
      being a cliff.
    */
    const CHUNK = 30;
    const idChunks: string[][] = [];
    for (let i = 0; i < classIds.length; i += CHUNK) {
      idChunks.push(classIds.slice(i, i + CHUNK));
    }

    if (!schoolIdForQuery) {
      setLoading(false);
      return;
    }

    /**
     * Subscribe one query per chunk and merge the results by document id.
     *
     * Each chunk arrives on its own schedule, so the merged view is rebuilt from
     * the per-chunk snapshots rather than appended to — appending would double
     * every row the moment one chunk updated.
     */
    const subscribeChunked = <T,>(
      build: (ids: string[]) => any,
      apply: (rows: T[]) => void,
      label: string
    ): (() => void)[] => {
      const perChunk = new Map<number, Map<string, T>>();
      const publish = () => {
        const merged = new Map<string, T>();
        for (const rows of perChunk.values()) {
          for (const [id, row] of rows) merged.set(id, row);
        }
        apply(Array.from(merged.values()));
      };

      return idChunks.map((ids, index) =>
        onSnapshot(
          build(ids),
          (snapshot: any) => {
            const rows = new Map<string, T>();
            snapshot.docs.forEach((d: any) => rows.set(d.id, { id: d.id, ...d.data() } as T));
            perChunk.set(index, rows);
            publish();
            setDataError(null);
          },
          (error: any) => {
            console.error(`TeacherDashboard: ${label} query failed`, error);
            setDataError(`${label} could not be read: ${error.message}`);
            setLoading(false);
          }
        )
      );
    };

    const unsubscribeStudents = subscribeChunked<any>(
      (ids) =>
        query(
          collection(db, 'users'),
          where('role', '==', 'student'),
          where('school_id', '==', schoolIdForQuery),
          where('class_ids', 'array-contains-any', ids)
        ),
      // Students are keyed by uid elsewhere on this page, not by doc id.
      (rows) => setStudents(rows.map((r: any) => ({ ...r, uid: r.id }))),
      'Students'
    );

    const unsubscribeScores = subscribeChunked<StudentScore>(
      (ids) =>
        query(
          collection(db, 'student_scores'),
          where('school_id', '==', schoolIdForQuery),
          where('class_id', 'in', ids),
          orderBy('completed_at', 'desc')
        ),
      (rows) => setScores(rows),
      'Student scores'
    );

    const unsubscribeLaunches = subscribeChunked<LessonLaunch>(
      (ids) =>
        query(
          collection(db, 'lesson_launches'),
          where('school_id', '==', schoolIdForQuery),
          where('class_id', 'in', ids),
          orderBy('launched_at', 'desc')
        ),
      (rows) => {
        setLaunches(rows);
        setLoading(false);
      },
      'Lesson launches'
    );

    return () => {
      [...unsubscribeStudents, ...unsubscribeScores, ...unsubscribeLaunches].forEach((off) => off());
    };
  }, [user?.uid, profile, allClasses]);

  // Class Performance reads the same scores snapshot, normalised once.
  const performanceResults = useMemo(
    () => scores.map((sc) => normaliseScore(sc as unknown as RawScore)),
    [scores]
  );
  const [passThreshold, setPassThreshold] = useState(DEFAULT_PASS_THRESHOLD);
  const [teacherSessions, setTeacherSessions] = useState<SessionSummaryRow[] | null>(null);
  useEffect(() => {
    let cancelled = false;
    getPassThreshold(profile?.school_id).then((t) => !cancelled && setPassThreshold(t));
    return () => {
      cancelled = true;
    };
  }, [profile?.school_id]);
  useEffect(() => {
    if (!user?.uid) return;
    let cancelled = false;
    // Enough for a school year of sessions; lessons conducted is counted from these.
    getRecentTeacherSessions(user.uid, 300)
      .then((rows) => !cancelled && setTeacherSessions(rows))
      .catch((err) => console.warn('[TeacherDashboard] sessions unavailable:', err));
    return () => {
      cancelled = true;
    };
  }, [user?.uid]);

  // Calculate class insights
  const classInsights = useMemo((): ClassInsight[] => {
    return allClasses.map(classItem => {
      const classStudents = students.filter(s => s.class_ids?.includes(classItem.id));
      const classScores = scores.filter(s => s.class_id === classItem.id);
      const classLaunches = launches.filter(l => l.class_id === classItem.id);
      const completedLaunches = classLaunches.filter(l => l.completion_status === 'completed');

      const averageScore = classScores.length > 0
        ? Math.round(classScores.reduce((sum, s) => sum + (s.score?.percentage || 0), 0) / classScores.length)
        : 0;

      return {
        classId: classItem.id,
        className: classItem.class_name,
        curriculum: classItem.curriculum,
        subject: classItem.subject,
        studentCount: classStudents.length,
        averageScore,
        totalQuizzes: classScores.length,
        completedLessons: completedLaunches.length,
        totalLessons: classLaunches.length,
        completionRate: classLaunches.length > 0
          ? Math.round((completedLaunches.length / classLaunches.length) * 100)
          : 0,
        isShared: sharedClasses.some(sc => sc.id === classItem.id),
        isOwner: managedClasses.some(mc => mc.id === classItem.id),
      };
    });
  }, [allClasses, students, scores, launches, sharedClasses, managedClasses]);

  // Class launch: fetch chapters when launch modal opens; dedupe so each chapter appears once, merge topics
  const fetchChaptersForLaunch = useCallback(async () => {
    setLaunchLessonModalLoading(true);
    try {
      const snap = await getDocs(collection(db, 'curriculum_chapters'));
      const raw = snap.docs
        .map((d) => ({ id: d.id, ...d.data(), topics: d.data()?.topics || [] }))
        .filter((ch: any) => (ch.topics?.length || 0) > 0);

      // Dedupe by logical chapter (same name + curriculum + class + subject) so each chapter shows once
      const key = (ch: any) =>
        [ch.chapter_number, ch.chapter_name, ch.curriculum, ch.class_name ?? ch.class, ch.subject].join('|');
      const byKey = new Map<string, any[]>();
      for (const ch of raw) {
        const k = key(ch);
        if (!byKey.has(k)) byKey.set(k, []);
        byKey.get(k)!.push(ch);
      }

      const list = Array.from(byKey.entries())
        .map(([_, docs]) => {
          const first = docs[0];
          const seenTopicIds = new Set<string>();
          const mergedTopics: any[] = [];
          for (const doc of docs) {
            for (const t of doc.topics || []) {
              const tid = t.topic_id ?? t.id;
              if (!tid || seenTopicIds.has(tid)) continue;
              seenTopicIds.add(tid);
              mergedTopics.push({ ...t, topic_id: tid, topic_name: t.topic_name || String(tid) });
            }
          }
          return {
            ...first,
            topics: mergedTopics.sort((a: any, b: any) => (a.topic_priority ?? 999) - (b.topic_priority ?? 999)),
          };
        })
        .filter((ch: any) => (ch.topics?.length || 0) > 0)
        .sort((a: any, b: any) => (a.chapter_number || 0) - (b.chapter_number || 0));

      setChaptersForLaunch(list);
      setSelectedLaunchChapterId('');
      setSelectedLaunchTopicId('');
    } catch (e) {
      console.error('Fetch chapters for launch:', e);
      toast.error('Failed to load lessons');
    } finally {
      setLaunchLessonModalLoading(false);
    }
  }, []);

  useEffect(() => {
    if (launchLessonModalOpen) fetchChaptersForLaunch();
  }, [launchLessonModalOpen, fetchChaptersForLaunch]);

  // Show session error to user
  useEffect(() => {
    if (sessionContextError) {
      toast.error(sessionContextError);
      clearSessionError();
    }
  }, [sessionContextError, clearSessionError]);

  // Subscribe to student profiles so we show current name (e.g. after they change it in profile)
  const progressStudentUids = useMemo(() => {
    const listUids = progressList.map((p) => p.student_uid);
    const reqUids = Array.isArray(activeSession?.join_requests) ? activeSession.join_requests : [];
    return [...new Set([...listUids, ...reqUids])].sort().join(',');
  }, [progressList, activeSession?.join_requests]);
  useEffect(() => {
    if (!activeSessionId || !progressStudentUids) {
      setStudentDisplayNames({});
      return;
    }
    const uids = progressStudentUids.split(',').filter(Boolean);
    const unsubs: Unsubscribe[] = uids.map((uid) =>
      onSnapshot(doc(db, 'users', uid), (snap) => {
        if (snap.exists()) {
          const d = snap.data();
          const name = resolveStudentDisplayName(
            {
              uid,
              name: typeof d?.name === 'string' ? d.name : null,
              displayName: typeof d?.displayName === 'string' ? d.displayName : null,
              email: typeof d?.email === 'string' ? d.email : null,
            },
            null,
            null
          );
          if (name) setStudentDisplayNames((prev) => ({ ...prev, [uid]: name }));
        }
      })
    );
    return () => {
      unsubs.forEach((u) => u());
    };
  }, [activeSessionId, progressStudentUids]);

  const copySessionCode = useCallback(() => {
    if (activeSession?.session_code) {
      navigator.clipboard.writeText(activeSession.session_code);
      toast.success('Session code copied');
    }
  }, [activeSession?.session_code]);

  const handleStartSession = useCallback(async () => {
    if (!sessionClassId) {
      toast.error('Select a class first');
      return;
    }
    const id = await startSession(sessionClassId);
    if (id) toast.success('Class session started. Share the code with students.');
  }, [sessionClassId, startSession]);

  const handleLaunchLesson = useCallback(async () => {
    if (!selectedLaunchChapterId || !selectedLaunchTopicId) {
      toast.error('Select a chapter and topic');
      return;
    }
    const ch = chaptersForLaunch.find((c: any) => c.id === selectedLaunchChapterId);
    const topic = ch?.topics?.find((t: any) => t.topic_id === selectedLaunchTopicId);
    if (!ch || !topic) return;
    const ok = await launchLessonToClass({
      chapter_id: selectedLaunchChapterId,
      topic_id: topic.topic_id,
      curriculum: ch.curriculum || '',
      class_name: String(ch.class_name ?? ch.class ?? ''),
      subject: ch.subject || '',
      // No longer a choice: the Three.js player is the classroom player. The
      // field is still sent so the value is explicit on the session document
      // and useEnforcedPlayerRoute keeps working.
      player: DEFAULT_PLAYER,
    });
    if (ok) {
      toast.success('Lesson launched to class');
      setLaunchLessonModalOpen(false);
    }
  }, [selectedLaunchChapterId, selectedLaunchTopicId, chaptersForLaunch, launchLessonToClass]);

  const handleEndSession = useCallback(async () => {
    const ok = await endSession();
    if (ok) toast.success('Session ended');
  }, [endSession]);

  const handleRemoveStudent = useCallback(
    async (studentUid: string) => {
      if (!activeSessionId || !user?.uid) return;
      setRemovingStudentUid(studentUid);
      try {
        const ok = await removeStudentFromSession(activeSessionId, user.uid, studentUid);
        if (ok) toast.success('Student removed from session');
        else toast.error('Failed to remove student');
      } finally {
        setRemovingStudentUid(null);
      }
    },
    [activeSessionId, user?.uid]
  );
  const [approvingStudentUid, setApprovingStudentUid] = useState<string | null>(null);

  const handleApproveJoin = useCallback(
    async (studentUid: string) => {
      if (!activeSessionId) return;
      setApprovingStudentUid(studentUid);
      try {
        const ok = await approveJoinRequest(activeSessionId, studentUid);
        if (ok) toast.success('Student join request approved');
        else toast.error('Failed to approve request');
      } finally {
        setApprovingStudentUid(null);
      }
    },
    [activeSessionId]
  );
  const handleLaunchVr360ToClass = useCallback(
    async (tour: (typeof VR360_TOURS)[number]) => {
      if (!launchLessonToClass) return;
      const payload = {
        chapter_id: VR360_TOUR_CHAPTER_ID,
        topic_id: topicIdForVr360TourId(tour.id),
        lesson_type: 'vr360_video' as const,
        vr360_tour_id: tour.id,
        curriculum: 'VR',
        class_name: '',
        subject: '360° Video Tour',
        lang: 'en',
      };
      const sessionId = activeSessionId;
      if (!sessionId) {
        toast.error('Start a class session first (select a class, then “Start class session”).');
        return;
      }
      const ok = await launchLessonToClass(payload, sessionId);
      if (ok) toast.success('360° video tour sent to class');
      else toast.error('Failed to launch tour to class');
    },
    [launchLessonToClass, activeSessionId]
  );

  // Open the launched lesson in Krpano so the teacher can control where the class looks (drag the 360° view)
  const [openLessonToControlLoading, setOpenLessonToControlLoading] = useState(false);
  const openLessonToControlView = useCallback(async () => {
    setOpenLessonToControlLoading(true);
    try {
      const result = await openLaunchedLessonAsHost({
        launched: activeSession?.launched_lesson,
        sessionId: activeSessionId,
        navigate,
      });
      if (!result.ok && result.reason !== 'no-launch') {
        toast.error(
          result.reason === 'tour-missing' ? '360° tour not found' : 'Could not open lesson. Try again.'
        );
      }
    } finally {
      setOpenLessonToControlLoading(false);
    }
  }, [activeSession?.launched_lesson, activeSessionId, navigate]);

  // Fetch skybox URL for "Student screens" thumbnails when there are students (with or without view yet)
  useEffect(() => {
    const launched = activeSession?.launched_lesson;
    if (!launched || progressList.length === 0) {
      setStudentScreensSkyboxUrl(null);
      return;
    }
    let cancelled = false;
    setStudentScreensSkyboxLoading(true);
    (async () => {
      try {
        const { getLessonBundle } = await import('../../services/firestore/getLessonBundle');
        const bundle = await getLessonBundle({
          chapterId: launched.chapter_id,
          topicId: launched.topic_id,
          lang: 'en',
          source: launched.lesson_type === 'user_generated' ? 'user_generated' : 'curriculum',
        });
        if (cancelled) return;
        const topic = bundle.chapter?.topics?.find((t: any) => t.topic_id === launched.topic_id) || bundle.chapter?.topics?.[0];
        const url =
          (bundle.skybox as any)?.imageUrl ||
          (bundle.skybox as any)?.file_url ||
          (topic as any)?.skybox_url ||
          '';
        setStudentScreensSkyboxUrl(url || null);
      } catch {
        if (!cancelled) setStudentScreensSkyboxUrl(null);
      } finally {
        if (!cancelled) setStudentScreensSkyboxLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [activeSession?.launched_lesson, progressList.length]);

  // Handle sharing/unsharing class. Through the server, which checks the class
  // is this teacher's and the other teacher is approved at the same school.
  const handleShareClass = async (classId: string, teacherId: string, share: boolean) => {
    if (!user?.uid) return;
    const result = await shareClass(classId, teacherId, share);
    if (!result.ok) {
      console.error('Error sharing class:', result.message);
      alert(`Failed to ${share ? 'share' : 'unshare'} class: ${result.message}`);
    }
  };

  const filteredClassInsights = selectedSubjectFilter === 'all'
    ? classInsights
    : classInsights.filter(ci => ci.subject === selectedSubjectFilter || (!ci.subject && selectedSubjectFilter === 'All Subjects'));

  // Only before anything has ever been shown. A refresh over existing
  // content must not blank the page.
  if (loading && !hasLoaded) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-2 border-primary/30 border-t-primary mx-auto mb-4" />
          <p className="text-muted-foreground">Loading your dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8 pb-6 border-b border-border">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-6">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 rounded-xl bg-primary/10 border border-border flex items-center justify-center">
                <FaChalkboardTeacher className="text-primary text-xl" />
              </div>
              <div>
                <h1 className="text-3xl font-bold mb-1" style={learnXRFontStyle}>
                  <span className="text-foreground">In</span>
                  <span className="text-primary">3D.ai</span>
                  <TrademarkSymbol />
                </h1>
                <h2 className="text-xl font-semibold text-foreground">Teacher Dashboard</h2>
                <div className="flex items-center gap-3 mt-1 flex-wrap">
                  <p className="text-muted-foreground text-sm">Precision insights for every class you lead.</p>
                  {schoolCode && (
                    <SchoolCodeBlock code={schoolCode} variant="dashboard" />
                  )}
                </div>
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              <Badge variant="secondary" className="text-xs">Realtime data</Badge>
              <Badge variant="secondary" className="text-xs">Collaboration ready</Badge>
              <Badge variant="secondary" className="text-xs">AI assisted</Badge>
            </div>
          </div>
        </div>

        {/* Quick action tiles */}
        <section className="mb-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          <Link
            to="/question-paper/generate"
            className="group rounded-xl border border-border bg-card p-4 hover:border-primary/60 transition"
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-primary/10 border border-border flex items-center justify-center">
                <FaClipboardList className="w-5 h-5 text-primary" />
              </div>
              <div className="min-w-0">
                <div className="text-sm font-semibold">AI Question Paper</div>
                <div className="text-xs text-muted-foreground truncate">Generate CBSE/RBSE papers</div>
              </div>
            </div>
          </Link>
          <Link
            to="/question-paper/library"
            className="group rounded-xl border border-border bg-card p-4 hover:border-primary/60 transition"
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-primary/10 border border-border flex items-center justify-center">
                <FaFileAlt className="w-5 h-5 text-primary" />
              </div>
              <div className="min-w-0">
                <div className="text-sm font-semibold">Paper Library</div>
                <div className="text-xs text-muted-foreground truncate">Saved question papers</div>
              </div>
            </div>
          </Link>
          <Link
            to="/admin/classes"
            className="group rounded-xl border border-border bg-card p-4 hover:border-primary/60 transition"
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-primary/10 border border-border flex items-center justify-center">
                <FaChalkboardTeacher className="text-primary" />
              </div>
              <div className="min-w-0">
                <div className="text-sm font-semibold">My Classes</div>
                <div className="text-xs text-muted-foreground truncate">Approvals, rosters and sharing</div>
              </div>
            </div>
          </Link>
          <Link
            to="/lessons"
            className="group rounded-xl border border-border bg-card p-4 hover:border-primary/60 transition"
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-primary/10 border border-border flex items-center justify-center">
                <FaVideo className="w-5 h-5 text-primary" />
              </div>
              <div className="min-w-0">
                <div className="text-sm font-semibold">Lessons</div>
                <div className="text-xs text-muted-foreground truncate">Browse content library</div>
              </div>
            </div>
          </Link>
        </section>

        {/* Class Session - Launch lesson to connected headsets */}
        <section className="mb-8" aria-labelledby="class-launch-heading">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
            <h2 id="class-launch-heading" className="text-xl font-semibold text-foreground flex items-center gap-2">
              <span className="flex items-center justify-center w-10 h-10 rounded-xl bg-primary/10 border border-primary/20">
                <FaVideo className="text-primary w-5 h-5" />
              </span>
              Class Launch
            </h2>
          </div>
          <Card className="border border-border bg-card shadow-sm overflow-hidden max-w-6xl w-full">
            <CardContent className="p-4 sm:p-6 lg:p-8">
              {!activeSessionId ? (
                <div className="flex flex-col gap-4 sm:gap-6">
                  <p className="text-muted-foreground text-sm sm:text-base max-w-xl">
                    Start a session and share the code with students. When you launch a lesson, everyone in the session will receive it on their device.
                  </p>
                  <div className="flex flex-col sm:flex-row sm:items-end gap-3 sm:gap-4">
                    <div className="w-full sm:min-w-[220px] sm:max-w-[280px]">
                      <label className="text-xs font-medium text-muted-foreground block mb-1.5">Class</label>
                      <Select value={sessionClassId} onValueChange={setSessionClassId}>
                        <SelectTrigger className="w-full">
                          <SelectValue placeholder="Select class" />
                        </SelectTrigger>
                        <SelectContent>
                          {allClasses.map((c) => (
                            <SelectItem key={c.id} value={c.id}>
                              {c.class_name} {c.subject ? `– ${c.subject}` : ''}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <Button
                      onClick={handleStartSession}
                      disabled={sessionContextLoading || allClasses.length === 0}
                      className="w-full sm:w-auto shrink-0"
                    >
                      {sessionContextLoading ? 'Starting…' : 'Start class session'}
                    </Button>
                  </div>
                </div>
              ) : (
                <>
                  {/* Two-column layout: Left = controls + students, Right = live preview */}
                  <div className="grid grid-cols-1 lg:grid-cols-[1fr,minmax(340px,440px)] xl:grid-cols-[1fr,minmax(360px,480px)] gap-6 lg:gap-8">
                    {/* ——— LEFT: Session controls & student list ——— */}
                    <div className="flex flex-col gap-4 sm:gap-6 min-w-0 order-2 lg:order-1">
                      {/* Session header: code + leave */}
                      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between pb-4 border-b border-border">
                        <div className="flex flex-col gap-2 min-w-0">
                          <p className="text-muted-foreground text-xs sm:text-sm">Session active · Students join with this code</p>
                          <div
                            className="flex items-center gap-2 sm:gap-3 p-3 sm:p-4 rounded-xl bg-primary/10 border border-primary/30 w-fit cursor-pointer hover:bg-primary/15 transition-colors active:scale-[0.99] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                            onClick={copySessionCode}
                            role="button"
                            tabIndex={0}
                            onKeyDown={(e) => e.key === 'Enter' && copySessionCode()}
                          >
                            <span className="font-mono text-xl sm:text-2xl font-bold tracking-widest text-primary select-all">
                              {activeSession?.session_code ?? '—'}
                            </span>
                            <FaCopy className="text-muted-foreground shrink-0 w-4 h-4" aria-hidden />
                            <span className="text-xs text-muted-foreground hidden sm:inline">Click to copy</span>
                          </div>
                        </div>
                        <Button variant="outline" size="sm" onClick={leaveSessionAsTeacher} className="shrink-0 self-start sm:self-center">
                          Leave session (local)
                        </Button>
                      </div>

                      {/* Actions */}
                      <p className="text-muted-foreground text-xs sm:text-sm max-w-xl">
                        To control where students look: open the lesson in the 360° player and drag the view — students will follow in real time.
                      </p>
                      <div className="flex flex-col sm:flex-row flex-wrap gap-2 sm:gap-3">
                        <Button onClick={() => setLaunchLessonModalOpen(true)} className="gap-2 w-full sm:w-auto shrink-0">
                          <FaVideo className="w-4 h-4 shrink-0" />
                          Launch lesson to class
                        </Button>
                        {activeSession?.launched_lesson && (
                          <Button
                            variant="outline"
                            className="gap-2 border-primary/50 text-primary hover:bg-primary/10 w-full sm:w-auto shrink-0"
                            onClick={openLessonToControlView}
                            disabled={openLessonToControlLoading}
                            title="Open the launched lesson so you can direct the class view by dragging the 360° scene"
                          >
                            {openLessonToControlLoading ? (
                              <>Loading…</>
                            ) : (
                              <>
                                <FaArrowRight className="w-4 h-4 shrink-0" />
                                <span className="hidden sm:inline">Open lesson to control class view</span>
                                <span className="sm:hidden">Control class view</span>
                              </>
                            )}
                          </Button>
                        )}
                        <Button variant="destructive" onClick={handleEndSession} disabled={sessionContextLoading} className="gap-2 w-full sm:w-auto shrink-0">
                          <FaStopCircle className="w-4 h-4 shrink-0" />
                          End session
                        </Button>
                      </div>

                      <div className="rounded-xl border border-border bg-muted/20 p-3 sm:p-4 space-y-2">
                        <p className="text-xs sm:text-sm font-medium text-foreground">360° video VR tours (krpano)</p>
                        <p className="text-[11px] sm:text-xs text-muted-foreground">Launch the same way as in Lessons: students in this session follow your view in real time after they open the tour.</p>
                        <div className="flex flex-wrap gap-2">
                          {VR360_TOURS.map((t) => (
                            <Button
                              key={t.id}
                              type="button"
                              variant="secondary"
                              size="sm"
                              className="text-xs h-8"
                              onClick={() => void handleLaunchVr360ToClass(t)}
                              disabled={!activeSessionId || sessionContextLoading}
                            >
                              {t.title}
                            </Button>
                          ))}
                        </div>
                      </div>

                      {/* Join Requests */}
                      {activeSession?.join_requests && activeSession.join_requests.length > 0 && (
                        <div className="pt-2 border-t border-border">
                          <h3 className="text-sm sm:text-base font-semibold text-amber-500 mb-2 flex items-center gap-2">
                            <FaBell className="w-4 h-4" />
                            Pending Join Requests
                          </h3>
                          <div className="space-y-2">
                            {activeSession.join_requests.map((uid) => {
                              const displayLabel = studentDisplayNames[uid] ?? resolveStudentDisplayName(null, null, { uid });
                              return (
                                <Card key={`req-${uid}`} className="border-amber-500/30 bg-amber-500/5">
                                  <CardContent className="p-3 sm:p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                                    <div className="flex items-center gap-3 min-w-0">
                                      <div className="w-10 h-10 rounded-full bg-amber-500/20 flex items-center justify-center shrink-0">
                                        <FaUserCheck className="text-amber-600 text-lg" />
                                      </div>
                                      <div>
                                        <p className="font-medium text-foreground truncate">{displayLabel}</p>
                                        <p className="text-xs text-muted-foreground">Wants to rejoin</p>
                                      </div>
                                    </div>
                                    <Button
                                      size="sm"
                                      className="shrink-0 gap-2 w-full sm:w-auto"
                                      onClick={() => handleApproveJoin(uid)}
                                      disabled={approvingStudentUid === uid}
                                    >
                                      {approvingStudentUid === uid ? 'Approving…' : 'Approve'}
                                    </Button>
                                  </CardContent>
                                </Card>
                              );
                            })}
                          </div>
                        </div>
                      )}

                      {/* Student list + chips (only when we have students) */}
                      {progressList.length > 0 && (
                        <div className="pt-2 border-t border-border">
                          <h3 className="text-sm sm:text-base font-semibold text-foreground mb-1">Students in session</h3>
                          <p className="text-xs text-muted-foreground mb-3">Click “View screen” or a chip to see their 360° view in the panel on the right.</p>
                          <div className="flex flex-wrap gap-2 mb-4">
                            {progressList.map((p) => {
                              const displayLabel = studentDisplayNames[p.student_uid] ?? resolveStudentDisplayName(null, null, {
                                uid: p.student_uid,
                                displayName: p.display_name,
                                email: p.email,
                              });
                              const phaseLabel = (() => {
                                const pn = String(p.phase || 'idle');
                                if (pn === 'intro') return 'Intro';
                                if (pn === 'explanation') return 'Explanation';
                                if (pn === 'exploration') return 'Exploration';
                                if (pn === 'outro') return 'Outro';
                                if (pn === 'quiz') return 'Quiz';
                                if (pn === 'completed') return 'Completed';
                                if (pn === 'loading') return 'Loading…';
                                return 'Waiting';
                              })();
                              const isSelected = selectedStudentForScreen === p.student_uid;
                              const hasView = p.student_view != null;
                              return (
                                <button
                                  key={p.student_uid}
                                  type="button"
                                  onClick={() => setSelectedStudentForScreen(isSelected ? null : p.student_uid)}
                                  className={`inline-flex items-center gap-2 rounded-xl border px-3 py-2 text-left text-sm transition-all ${
                                    isSelected
                                      ? 'border-primary bg-primary/10 text-primary ring-2 ring-primary/20'
                                      : 'border-border bg-card hover:bg-accent/30 hover:border-primary/40'
                                  }`}
                                >
                                  <FaVideo className="w-3.5 h-3.5 shrink-0 opacity-70" />
                                  <span className="font-medium truncate max-w-[120px] sm:max-w-[140px]">{displayLabel}</span>
                                  <Badge variant={isSelected ? 'default' : 'secondary'} className="text-[10px] shrink-0">
                                    {phaseLabel}
                                  </Badge>
                                  {hasView && <span className="w-1.5 h-1.5 rounded-full bg-green-500 shrink-0" title="View available" />}
                                </button>
                              );
                            })}
                          </div>
                          <div className="space-y-2 sm:space-y-3">
                            {progressList.map((p) => {
                              const phaseLabel = (() => {
                                const pn = String(p.phase || 'idle');
                                if (pn === 'intro') return 'Intro';
                                if (pn === 'explanation') return 'Explanation';
                                if (pn === 'exploration') return 'Exploration';
                                if (pn === 'outro') return 'Outro';
                                if (pn === 'quiz') return 'Quiz';
                                if (pn === 'completed') return 'Completed';
                                if (pn === 'loading') return 'Loading…';
                                if (pn === 'idle') return 'Waiting';
                                return 'Waiting';
                              })();
                              const isComplete = p.phase === 'completed';
                              const isQuiz = p.phase === 'quiz';
                              const displayLabel = studentDisplayNames[p.student_uid] ?? resolveStudentDisplayName(null, null, {
                                uid: p.student_uid,
                                displayName: p.display_name,
                                email: p.email,
                              });
                              const lastUpdated = p.last_updated ? (typeof p.last_updated === 'string' ? new Date(p.last_updated) : (p.last_updated as { toDate?: () => Date })?.toDate?.() ?? null) : null;
                              const hasQuizData = p.quiz_score != null && p.quiz_total != null && (p.quiz_total as number) > 0;
                              const quizAnswers = (p.quiz_answers as Array<{ question_index: number; correct: boolean; selected_option_index: number }> | undefined) ?? [];
                              return (
                                <Card key={p.student_uid} className={`border transition-colors ${isComplete ? 'border-primary/50 bg-primary/5' : 'border-border bg-card hover:border-border/80'}`}>
                                  <CardContent className="p-3 sm:p-4 space-y-2 sm:space-y-3">
                                    <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                                      <div className="flex items-center gap-2 min-w-0 flex-1">
                                        <div className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${isComplete ? 'bg-primary' : isQuiz ? 'bg-amber-500 animate-pulse' : 'bg-muted-foreground'}`} />
                                        <span className="font-medium text-foreground truncate text-sm sm:text-base" title={displayLabel}>{displayLabel}</span>
                                      </div>
                                      <div className="flex flex-wrap items-center gap-2">
                                        <Badge variant={isComplete ? 'default' : isQuiz ? 'secondary' : 'outline'} className="font-normal text-xs">
                                          {phaseLabel}
                                        </Badge>
                                        {lastUpdated && (
                                          <span className="text-xs text-muted-foreground whitespace-nowrap">
                                            {lastUpdated.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
                                          </span>
                                        )}
                                        <div className="flex gap-1">
                                          <Button
                                            variant="outline"
                                            size="sm"
                                            className="shrink-0 gap-1 h-8 text-xs"
                                            onClick={() => setSelectedStudentForScreen(selectedStudentForScreen === p.student_uid ? null : p.student_uid)}
                                            title="View this student's screen"
                                          >
                                            <FaVideo className="w-3.5 h-3.5 shrink-0" />
                                            <span className="hidden sm:inline">{selectedStudentForScreen === p.student_uid ? 'Hide' : 'View screen'}</span>
                                          </Button>
                                          <Button
                                            variant="ghost"
                                            size="sm"
                                            className="text-destructive hover:text-destructive hover:bg-destructive/10 shrink-0 h-8 w-8 p-0"
                                            onClick={() => handleRemoveStudent(p.student_uid)}
                                            disabled={removingStudentUid === p.student_uid}
                                            title="Remove from session"
                                          >
                                            {removingStudentUid === p.student_uid ? (
                                              <span className="animate-pulse text-xs">…</span>
                                            ) : (
                                              <FaUserMinus className="w-4 h-4" />
                                            )}
                                          </Button>
                                        </div>
                                      </div>
                                    </div>
                                    {p.student_view != null && (
                                      <div className="pt-2 border-t border-border/60 flex items-center gap-2">
                                        <span className="text-xs text-muted-foreground">360°:</span>
                                        <span className="text-xs font-mono text-foreground/80">
                                          h {Math.round(p.student_view.hlookat)}° v {Math.round(p.student_view.vlookat)}°
                                        </span>
                                      </div>
                                    )}
                                    {hasQuizData && (
                                      <div className="pt-2 border-t border-border/60">
                                        <p className="text-xs font-medium text-muted-foreground mb-1.5">
                                          Quiz: {String(p.quiz_score)}/{String(p.quiz_total)}
                                        </p>
                                        {quizAnswers.length > 0 && (
                                          <div className="flex flex-wrap gap-1">
                                            {quizAnswers
                                              .slice()
                                              .sort((a, b) => a.question_index - b.question_index)
                                              .map((a, i) => (
                                                <span
                                                  key={i}
                                                  className={`inline-flex items-center justify-center w-6 h-5 rounded text-[10px] font-medium ${a.correct ? 'bg-primary/20 text-primary' : 'bg-destructive/20 text-destructive'}`}
                                                  title={a.correct ? `Q${a.question_index + 1} correct` : `Q${a.question_index + 1} wrong`}
                                                >
                                                  {a.correct ? '✓' : '✗'}
                                                </span>
                                              ))}
                                          </div>
                                        )}
                                      </div>
                                    )}
                                  </CardContent>
                                </Card>
                              );
                            })}
                          </div>
                        </div>
                      )}
                    </div>

                    {/* ——— RIGHT: Live preview panel (sticky on desktop) ——— */}
                    <div className="lg:sticky lg:top-4 order-1 lg:order-2 h-fit">
                      <div className="rounded-xl border border-border bg-card shadow-panel overflow-hidden flex flex-col min-h-[240px] sm:min-h-[280px]">
                        {/* Preview header */}
                        <div className="flex items-center justify-between gap-3 px-4 py-3.5 border-b border-border bg-muted/40">
                          <div className="flex items-center gap-2.5 min-w-0">
                            <span className="flex items-center justify-center w-9 h-9 rounded-lg bg-primary/10 border border-primary/20 shrink-0">
                              <FaVideo className="w-4 h-4 text-primary" />
                            </span>
                            <div className="min-w-0">
                              <h3 className="text-sm font-semibold text-foreground truncate">Student screen</h3>
                              <p className="text-xs text-muted-foreground truncate mt-0.5">
                                {selectedStudentForScreen
                                  ? (studentDisplayNames[selectedStudentForScreen] ?? (() => {
                                      const progress = progressList.find((x) => x.student_uid === selectedStudentForScreen);
                                      return resolveStudentDisplayName(null, null, {
                                        uid: selectedStudentForScreen,
                                        displayName: progress?.display_name,
                                        email: progress?.email,
                                      });
                                    })())
                                  : 'Live 360° preview'}
                              </p>
                            </div>
                          </div>
                          {selectedStudentForScreen && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => setSelectedStudentForScreen(null)}
                              className="shrink-0 h-8 px-3 text-xs rounded-lg"
                            >
                              Close
                            </Button>
                          )}
                        </div>
                        {/* Preview viewport */}
                        <div className="flex-1 min-h-[220px] sm:min-h-[260px] aspect-video max-h-[50vh] lg:max-h-[440px] bg-muted/5 flex items-stretch justify-center overflow-hidden">
                          {!(studentScreensSkyboxLoading || studentScreensSkyboxUrl) ? (
                            <div className="w-full flex flex-col items-center justify-center gap-4 p-8 bg-muted/10 border border-dashed border-border/80 rounded-lg m-3">
                              <div className="w-16 h-16 rounded-2xl bg-primary/5 flex items-center justify-center border border-border/50">
                                <FaVideo className="w-8 h-8 text-muted-foreground/50" />
                              </div>
                              <div className="space-y-1 text-center">
                                <p className="text-sm font-medium text-muted-foreground">Launch a lesson to enable live 360° preview</p>
                                <p className="text-xs text-muted-foreground/80">Use “Launch lesson to class” on the left</p>
                              </div>
                            </div>
                          ) : selectedStudentForScreen ? (() => {
                            const p = progressList.find((x) => x.student_uid === selectedStudentForScreen);
                            if (!p) {
                              return (
                                <div className="w-full flex flex-col items-center justify-center gap-2 p-6 bg-muted/10 m-3 rounded-lg border border-border/50">
                                  <p className="text-sm text-muted-foreground">Student not found</p>
                                </div>
                              );
                            }
                            const displayLabel = studentDisplayNames[p.student_uid] ?? resolveStudentDisplayName(null, null, {
                              uid: p.student_uid,
                              displayName: p.display_name,
                              email: p.email,
                            });
                            const phaseLabel = (() => {
                              const pn = String(p.phase || 'idle');
                              if (pn === 'intro') return 'Intro';
                              if (pn === 'explanation') return 'Explanation';
                              if (pn === 'exploration') return 'Exploration';
                              if (pn === 'outro') return 'Outro';
                              if (pn === 'quiz') return 'Quiz';
                              if (pn === 'completed') return 'Completed';
                              if (pn === 'loading') return 'Loading…';
                              return 'Waiting';
                            })();
                            if (studentScreensSkyboxLoading) {
                              return (
                                <div className="w-full h-full flex items-center justify-center bg-muted/20">
                                  <span className="text-muted-foreground text-sm">Loading…</span>
                                </div>
                              );
                            }
                            if (studentScreensSkyboxUrl && p.student_view != null) {
                              return (
                                <div className="w-full h-full min-h-0 flex flex-col rounded-b-2xl overflow-hidden">
                                  <StudentScreen360Preview
                                    skyboxUrl={studentScreensSkyboxUrl}
                                    view={p.student_view}
                                    studentName={displayLabel}
                                    phaseLabel={phaseLabel}
                                    getApiBaseUrl={getApiBaseUrl}
                                    className="w-full flex-1 min-h-0 rounded-none"
                                  />
                                </div>
                              );
                            }
                            return (
                              <div className="w-full h-full flex flex-col items-center justify-center gap-4 p-6 bg-muted/20 m-3 rounded-lg border border-border/50">
                                <div className="w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center shrink-0 border border-primary/20">
                                  <FaUsers className="text-primary/60 w-8 h-8" />
                                </div>
                                <div className="space-y-1 text-center">
                                  <p className="text-sm font-medium text-foreground">{displayLabel}</p>
                                  <p className="text-xs text-muted-foreground">Waiting for view — student will appear when they look around</p>
                                  <p className="text-xs text-muted-foreground/80">{phaseLabel}</p>
                                </div>
                              </div>
                            );
                          })() : (
                            <div className="w-full flex flex-col items-center justify-center gap-4 p-8 bg-muted/10 border border-dashed border-border/80 rounded-lg m-3">
                              <div className="w-16 h-16 rounded-2xl bg-primary/5 flex items-center justify-center border border-border/50">
                                <FaVideo className="w-8 h-8 text-muted-foreground/50" />
                              </div>
                              <div className="space-y-1 text-center">
                                <p className="text-sm font-medium text-muted-foreground">Select a student to view their screen</p>
                                <p className="text-xs text-muted-foreground/80">Use the list on the left or click a student chip</p>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        </section>

        {/* Teaching & Creation Tools - Create (includes AI Teacher Support in top-right) */}
        <div className="mb-8">
          <h2 className="text-xl font-semibold text-foreground mb-4 flex items-center gap-2">
            <FaMagic className="text-primary" />
            Teaching & Creation Tools
          </h2>
          <Link to="/main">
            <Card className="border border-border bg-card hover:bg-accent/20 hover:border-primary/40 transition-all cursor-pointer group max-w-2xl">
              <CardContent className="p-6">
                <div className="flex items-start gap-4">
                  <div className="w-14 h-14 rounded-xl bg-primary/10 border border-primary/20 flex items-center justify-center shrink-0 group-hover:bg-primary/20 transition-colors">
                    <FaFlask className="text-primary text-2xl" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="text-lg font-semibold text-foreground mb-1">Create</h3>
                    <p className="text-muted-foreground text-sm mb-4">
                      Generate 360-degree skybox environments and 3D assets. Use AI Teacher Support in the top-right for lesson plans, content ideas, and grading rubrics.
                    </p>
                    <div className="flex flex-wrap gap-2">
                      <Badge variant="secondary" className="text-xs">Skybox</Badge>
                      <Badge variant="secondary" className="text-xs">3D Assets</Badge>
                      <Badge variant="secondary" className="text-xs">AI Teacher Support</Badge>
                      <Badge variant="secondary" className="text-xs">XR Ready</Badge>
                    </div>
                    <div className="flex items-center gap-2 mt-4 text-primary text-sm font-medium">
                      <span>Open Create Studio</span>
                      <FaArrowRight className="w-4 h-4" />
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </Link>
        </div>

        {/* Student Approvals */}
        {myPendingStudents.length > 0 && (
          <div className="mb-8">
            <Card className="border border-amber-500/40 bg-card">
              <CardContent className="p-6">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                  <div className="flex items-center gap-4">
                    <div className="w-14 h-14 rounded-lg bg-primary/10 border border-border flex items-center justify-center relative">
                      <FaUserCheck className="text-primary text-2xl" />
                      <Badge className="absolute -top-2 -right-2 h-6 w-6 rounded-full p-0 flex items-center justify-center bg-destructive text-destructive-foreground">
                        {myPendingStudents.length}
                      </Badge>
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <h2 className="text-xl font-semibold text-foreground">Student Approvals</h2>
                        <Badge variant="secondary" className="flex items-center gap-1">
                          <FaBell className="text-xs" />
                          {myPendingStudents.length} pending
                        </Badge>
                      </div>
                      <p className="text-muted-foreground text-sm mt-1">
                        You have {myPendingStudents.length} student(s) waiting for approval.
                      </p>
                    </div>
                  </div>
                  <Link to="/admin/classes?tab=approvals">
                    <Button variant="secondary" className="gap-2">
                      Review Approvals
                      <FaArrowRight className="w-4 h-4" />
                    </Button>
                  </Link>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Class Performance */}
        <div className="mb-8">
          <ClassPerformanceWorkspace
            classes={allClasses.map((c) => ({ id: c.id, name: c.class_name || 'Class' }))}
            students={students.map((st: any) => ({
              uid: st.uid,
              name: studentDisplayNames[st.uid] ?? resolveStudentDisplayName(st, null, { uid: st.uid }),
              email: st.email ?? null,
              classIds: Array.isArray(st.class_ids) ? st.class_ids : [],
            }))}
            results={performanceResults}
            sessions={teacherSessions}
            passThreshold={passThreshold}
            loading={loading && !hasLoaded}
            error={dataError}
          />
        </div>

        {user?.uid && (
          <div className="mb-8">
            <RecentSessions
              teacherUid={user.uid}
              classNames={Object.fromEntries(allClasses.map((c) => [c.id, c.class_name || 'Class']))}
            />
          </div>
        )}

        {/* Class Insights with Sharing */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold text-foreground flex items-center gap-2">
              <FaBook className="text-primary" />
              Class Insights
            </h2>
            <div className="flex items-center gap-2">
              <FaFilter className="text-muted-foreground" />
              <Select value={selectedSubjectFilter} onValueChange={setSelectedSubjectFilter}>
                <SelectTrigger className="w-[180px] bg-card border-border">
                  <SelectValue placeholder="All Subjects" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Subjects</SelectItem>
                  {Array.from(new Set(classInsights.map(ci => ci.subject || 'All Subjects'))).map(subject => (
                    <SelectItem key={subject} value={subject}>{subject}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          {filteredClassInsights.length === 0 ? (
            <Card className="border-border bg-card">
              <CardContent className="p-8 text-center">
                <FaBook className="text-4xl text-muted-foreground mx-auto mb-4" />
                <p className="text-muted-foreground">No classes available</p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {filteredClassInsights.map((insight) => {
                const classItem = allClasses.find(c => c.id === insight.classId);
                const isOwner = insight.isOwner;
                const sharedWith = classItem?.shared_with_teachers || [];

                return (
                  <Card
                    key={insight.classId}
                    className={`border p-4 transition-colors ${insight.isShared ? 'border-primary/30 bg-primary/5' : 'border-border bg-card'}`}
                  >
                    <CardContent className="p-0">
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <h3 className="text-foreground font-medium">{insight.className}</h3>
                            {insight.isShared && <Badge variant="secondary">Shared</Badge>}
                          </div>
                          <p className="text-muted-foreground text-sm mt-1">
                            {insight.curriculum} • {insight.subject || 'All Subjects'}
                          </p>
                        </div>
                        {isOwner && (
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => setSharingClassId(sharingClassId === insight.classId ? null : insight.classId)}
                            title="Share class with other teachers"
                          >
                            <FaShareAlt className="text-primary" />
                          </Button>
                        )}
                      </div>

                      {isOwner && sharingClassId === insight.classId && (
                        <div className="mb-3 p-3 bg-muted/50 rounded-lg border border-border">
                          <p className="text-foreground text-sm mb-2 font-medium">Share with teachers:</p>
                          <div className="space-y-2 max-h-40 overflow-y-auto">
                            {allTeachers.length === 0 ? (
                              <p className="text-muted-foreground text-xs">No other teachers in your school</p>
                            ) : (
                              allTeachers.map(teacher => {
                                const isShared = sharedWith.includes(teacher.uid);
                                return (
                                  <div key={teacher.uid} className="flex items-center justify-between text-sm">
                                    <span className="text-muted-foreground">
                                      {teacher.name || teacher.displayName || teacher.email}
                                    </span>
                                    <Button
                                      size="sm"
                                      variant={isShared ? 'secondary' : 'outline'}
                                      onClick={() => handleShareClass(insight.classId, teacher.uid, !isShared)}
                                    >
                                      {isShared ? <><FaUnlock className="mr-1 text-xs" /> Shared</> : <><FaLock className="mr-1 text-xs" /> Share</>}
                                    </Button>
                                  </div>
                                );
                              })
                            )}
                          </div>
                        </div>
                      )}

                      <div className="space-y-2 text-sm">
                        <div className="flex items-center justify-between">
                          <span className="text-muted-foreground">Students</span>
                          <span className="text-foreground">{insight.studentCount}</span>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-muted-foreground">Avg Score</span>
                          <span className={`font-medium ${insight.averageScore >= 70 ? 'text-primary' : insight.averageScore >= 50 ? 'text-amber-500' : 'text-destructive'}`}>
                            {insight.averageScore}%
                          </span>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-muted-foreground">Completion</span>
                          <span className="text-foreground">{insight.completedLessons} / {insight.totalLessons}</span>
                        </div>
                        <Progress value={insight.completionRate} className="h-2 mt-2" />
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </div>

        {/* Recent Student Activity */}
        <div>
          <h2 className="text-xl font-semibold text-foreground mb-4">Recent Student Activity</h2>
          {/*
            An empty page and a failed read are not the same thing, and used to
            look identical. Saying which is the difference between "nobody has
            taken a quiz" and "the query was rejected and the marks are sitting
            there unseen".
          */}
          {dataError ? (
            <Card className="border-destructive/40 bg-destructive/5">
              <CardContent className="p-8 text-center">
                <FaChartLine className="text-4xl text-destructive mx-auto mb-4" />
                <p className="font-semibold text-foreground">Results could not be loaded</p>
                <p className="mt-1 text-sm text-muted-foreground">{dataError}</p>
              </CardContent>
            </Card>
          ) : scores.length === 0 ? (
            <Card className="border-border bg-card">
              <CardContent className="p-8 text-center">
                <FaChartLine className="text-4xl text-muted-foreground mx-auto mb-4" />
                <p className="text-muted-foreground">No student activity yet</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-3">
              {scores.slice(0, 10).map((score) => {
                const student = students.find(s => s.uid === score.student_id);
                return (
                  <Card key={score.id} className="border-border bg-card hover:bg-accent/30 transition-colors">
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <h3 className="text-foreground font-medium">
                              {student?.name || student?.displayName || 'Unknown Student'}
                            </h3>
                            {student?.approvalStatus && (
                              <Badge variant={student.approvalStatus === 'approved' ? 'default' : student.approvalStatus === 'pending' ? 'secondary' : 'destructive'}>
                                {student.approvalStatus}
                              </Badge>
                            )}
                          </div>
                          <p className="text-muted-foreground text-sm mt-1">
                            {score.subject} - {score.curriculum} Class {score.class_name}
                          </p>
                          <p className="text-muted-foreground/70 text-xs mt-1">
                            Chapter: {score.chapter_id} • Attempt #{score.attempt_number}
                          </p>
                        </div>
                        <div className="ml-4 text-right">
                          <div className={`text-2xl font-bold ${score.score.percentage >= 70 ? 'text-primary' : score.score.percentage >= 50 ? 'text-amber-500' : 'text-destructive'}`}>
                            {score.score.percentage}%
                          </div>
                          <p className="text-muted-foreground text-sm">{score.score.correct}/{score.score.total}</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Launch lesson to class modal – visual card-based selection */}
      <Dialog open={launchLessonModalOpen} onOpenChange={setLaunchLessonModalOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] flex flex-col" aria-describedby={undefined}>
          <DialogHeader>
            <DialogTitle className="text-foreground">Launch lesson to class</DialogTitle>
            <DialogDescription className="sr-only">Select a chapter and topic to launch to all students in the class session.</DialogDescription>
          </DialogHeader>
          <p className="text-muted-foreground text-sm">Choose a chapter, then a topic. All joined students will receive this lesson.</p>
          {launchLessonModalLoading ? (
            <div className="py-6 text-center text-muted-foreground">Loading lessons…</div>
          ) : (
            <div className="flex flex-col gap-4 flex-1 min-h-0">
              {/* Chapter cards */}
              <div>
                <label className="text-xs font-medium text-foreground block mb-2">Chapter</label>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-48 overflow-y-auto pr-1">
                  {chaptersForLaunch.map((ch: any) => {
                    const selected = ch.id === selectedLaunchChapterId;
                    const meta = [ch.curriculum, ch.class_name ? `Class ${ch.class_name}` : '', ch.subject].filter(Boolean).join(' · ');
                    return (
                      <button
                        key={ch.id}
                        type="button"
                        onClick={() => { setSelectedLaunchChapterId(ch.id); setSelectedLaunchTopicId(''); }}
                        className={`text-left rounded-lg border-2 p-3 transition-all ${
                          selected
                            ? 'border-primary bg-primary/10 ring-1 ring-primary/30'
                            : 'border-border bg-card hover:border-primary/50 hover:bg-accent/20'
                        }`}
                      >
                        <div className="flex gap-3">
                          <div className="w-16 h-16 rounded-md bg-muted flex-shrink-0 flex items-center justify-center text-muted-foreground">
                            <FaBook className="w-6 h-6" />
                          </div>
                          <div className="min-w-0 flex-1">
                            <p className="font-medium text-foreground truncate">
                              {ch.chapter_number != null ? `Ch ${ch.chapter_number}. ` : ''}{ch.chapter_name || ch.id}
                            </p>
                            {meta && <p className="text-xs text-muted-foreground truncate">{meta}</p>}
                            <p className="text-xs text-muted-foreground/80 mt-0.5">{ch.topics?.length || 0} topic(s)</p>
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
              {/* Topic list when chapter selected */}
              {selectedLaunchChapterId && (() => {
                const ch = chaptersForLaunch.find((c: any) => c.id === selectedLaunchChapterId);
                const topics = ch?.topics || [];
                return (
                  <div>
                    <label className="text-xs font-medium text-foreground block mb-2">Topic</label>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-44 overflow-y-auto pr-1">
                      {topics.map((t: any, idx: number) => {
                        const selected = t.topic_id === selectedLaunchTopicId;
                        const label = (t.topic_priority != null ? `${t.topic_priority}. ` : `${idx + 1}. `) + (t.topic_name || t.topic_id);
                        return (
                          <button
                            key={t.topic_id}
                            type="button"
                            onClick={() => setSelectedLaunchTopicId(t.topic_id)}
                            className={`text-left rounded-lg border-2 px-3 py-2.5 transition-all ${
                              selected ? 'border-primary bg-primary/10' : 'border-border bg-card hover:border-primary/50 hover:bg-accent/20'
                            }`}
                          >
                            <p className="text-sm font-medium text-foreground truncate">{label}</p>
                            {t.learning_objective && (
                              <p className="text-xs text-muted-foreground truncate mt-0.5">{t.learning_objective}</p>
                            )}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                );
              })()}
              {/* Summary */}
              {selectedLaunchChapterId && selectedLaunchTopicId && (() => {
                const ch = chaptersForLaunch.find((c: any) => c.id === selectedLaunchChapterId);
                const topic = ch?.topics?.find((t: any) => t.topic_id === selectedLaunchTopicId);
                if (!ch || !topic) return null;
                const meta = [ch.curriculum, ch.class_name ? `Class ${ch.class_name}` : ''].filter(Boolean).join(' · ');
                return (
                  <div className="rounded-lg border-2 border-primary/40 bg-primary/10 p-3 space-y-1 flex-shrink-0">
                    <p className="text-xs font-medium text-muted-foreground">You will launch</p>
                    <p className="text-sm font-semibold text-foreground">
                      {ch.chapter_name || ch.id} → {topic.topic_name || topic.topic_id}
                    </p>
                    {meta && <p className="text-xs text-muted-foreground">{meta}</p>}
                  </div>
                );
              })()}
            </div>
          )}
          <DialogFooter className="gap-2 sm:gap-0 flex-shrink-0">
            <Button variant="outline" onClick={() => setLaunchLessonModalOpen(false)}>Cancel</Button>
            <Button onClick={handleLaunchLesson} disabled={!selectedLaunchChapterId || !selectedLaunchTopicId || sessionContextLoading}>
              Launch to class
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TeacherDashboard;
